<template>
  <div class="switch-automation-indicator" :title="titleText">
    <TimeSwitchDayRing
      v-if="isTimeSwitch"
      :config="automation.config"
      :color="accentColor"
      :size="24"
      :stroke-width="5"
      :show-now="true" />
    <template v-else-if="isMotionSwitch">
      <!-- Motion detected: animated walking man (accent = host widget colour) -->
      <div v-if="motionActive" class="motion-indicator" :style="accent ? { '--motion-accent': accent } : {}">
        <span class="material-symbols-outlined motion-indicator__walk">directions_walk</span>
      </div>
      <!-- Off-countdown: live circular countdown ring -->
      <AutomationProgressRing
        v-else-if="automation.isActive"
        :ratio="liveRatio"
        :color="accentColor"
        :size="28"
        :stroke-width="5"
        icon="lightbulb" />
      <!-- Idle: automation armed, waiting for motion (same slot, muted) -->
      <div v-else class="motion-indicator motion-indicator--idle">
        <span class="material-symbols-outlined motion-indicator__idle">directions_walk</span>
      </div>
    </template>
    <AutomationProgressRing
      v-else
      :ratio="automation.progressRatio"
      :color="accentColor"
      :size="24"
      :stroke-width="5" />
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import type { SwitchAutomationDisplay } from '../../utils/switchAutomation';
import { formatCountdown, useSecondsRemaining } from '../../composables/useAutomationBadge';
import AutomationProgressRing from './AutomationProgressRing.vue';
import TimeSwitchDayRing from './TimeSwitchDayRing.vue';
import { tt } from '../../utils/consoleText';

interface Props {
  automation: SwitchAutomationDisplay;
  /** Optional accent override (e.g. the light's current colour) */
  accent?: string | null;
}

const props = defineProps<Props>();

const isTimeSwitch = computed(() => props.automation.automationType === 'time_switch');
const isMotionSwitch = computed(() => props.automation.automationType === 'light_motion_switch');
const motionActive = computed(() => props.automation.motionActive === true);

/**
 * The backend only seeds time_remaining_seconds at the countdown edges, so the
 * ring counts down locally between socket updates (drift-resynced > 1.5 s).
 */
const liveRemaining = useSecondsRemaining(() => props.automation.remainingSeconds ?? null);

const liveRatio = computed(() => {
  const total = props.automation.totalSeconds;
  if (!total || total <= 0) return props.automation.progressRatio;
  return Math.min(1, Math.max(0, liveRemaining.value / total));
});

const liveCountdownText = computed(() => (liveRemaining.value > 0 ? formatCountdown(liveRemaining.value * 1000) : ''));

const accentColor = computed(() => {
  if (props.accent) return props.accent;
  if (isTimeSwitch.value) return 'rgb(14, 165, 233)';
  if (isMotionSwitch.value) return 'rgb(245, 158, 11)';
  return 'rgb(16, 185, 129)';
});

const titleText = computed(() => {
  if (isTimeSwitch.value) {
    return `${props.automation.name}: ${props.automation.detailLabel}${props.automation.countdownText ? ` • ${tt('next', 'naslednje')} ${props.automation.countdownText}` : ''}`;
  }
  if (isMotionSwitch.value) {
    const countdown = liveCountdownText.value || props.automation.countdownText;
    return `${props.automation.name}: ${countdown || props.automation.stateLabel}`;
  }
  return `${props.automation.name}: ${props.automation.countdownText || props.automation.stateLabel}`;
});
</script>
